import { z } from "zod";

export const salesLeadSchema = z
  .object({
    name: z.string().trim().min(1, "Company name is required"),
    email: z.string().trim().min(1, "Email is required").email("Invalid email format"),
    // Phone is optional. When provided, it must still be a valid 8–12 digit number.
    phone: z
      .string()
      .trim()
      .optional()
      .refine((val) => !val || /^\d+$/.test(val), {
        message: "Phone number must only contain digits",
      })
      .refine((val) => !val || (val.length >= 8 && val.length <= 12), {
        message: "Phone number must be between 8 and 12 digits",
      }),
    address: z.string().trim().min(1, "Address is required"),
    salesRepresentative: z.string().trim().min(1, "Contact person is required"),
    assignedTo: z.string().trim().min(1, "Assigned to is required"),
    status: z.string().trim().optional(),
    estimatedAnnualAppSales: z
      .any()
      .refine((val) => val === "" || val === undefined || val === null || !isNaN(Number(val)), {
        message: "Estimated Annual App Sales must be a number",
      })
      .transform((val) => (val === "" || val === undefined || val === null ? undefined : Number(val)))
      .pipe(z.number().min(0, "Estimated Annual App Sales must be 0 or greater").optional()),
    proposedFee: z
      .any()
      .refine((val) => val === "" || val === undefined || val === null || !isNaN(Number(val)), {
        message: "Proposed Fee must be a number",
      })
      .transform((val) => (val === "" || val === undefined || val === null ? undefined : Number(val)))
      .pipe(
        z
          .number()
          .min(0, "Proposed Fee must be between 0 and 100")
          .max(100, "Proposed Fee must be between 0 and 100")
          .optional(),
      ),
    notes: z
      .string()
      .trim()
      .optional()
      .or(z.literal("")),
    estimatedAnnualAppFee: z
      .any()
      .refine((val) => val === "" || val === undefined || val === null || !isNaN(Number(val)), {
        message: "Estimated Annual App Fee must be a number",
      })
      .transform((val) => (val === "" || val === undefined || val === null ? undefined : Number(val)))
      .pipe(z.number().min(0, "Estimated Annual App Fee must be 0 or greater").optional()),
    estimatedConversionRate: z
      .any()
      .refine((val) => val === "" || val === undefined || val === null || !isNaN(Number(val)), {
        message: "Estimated Conversion % must be a number",
      })
      .transform((val) => (val === "" || val === undefined || val === null ? undefined : Number(val)))
      .pipe(
        z
          .number()
          .min(0, "Estimated Conversion % must be between 0 and 1")
          .max(1, "Estimated Conversion % must be between 0 and 1")
          .optional(),
      ),
  })
  .passthrough();

const createLeadSchema = salesLeadSchema.extend({
  status: z.enum(["cold", "warm", "hot"], { message: "Status is required" }),
});

export const parseSalesLeadErrors = (data, { isEdit = false } = {}) => {
  const schema = isEdit ? salesLeadSchema : createLeadSchema;
  const result = schema.safeParse(data);
  if (result.success) return {};
  const errors = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join(".");
    if (!errors[key]) errors[key] = issue.message;
  }
  return errors;
};
